import React, { useEffect, useState } from "react";
import SearchBox from "@/components/SearchBox";
import ResultsView from "@/components/ResultsView";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Camera, Link2, Sparkles, Star } from "lucide-react";

export default function Home({ onRequireAuth }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [trending, setTrending] = useState([]);

  useEffect(() => {
    api
      .get("/trending")
      .then((r) => setTrending(r.data))
      .catch(() => {});
  }, []);

  const onSubmitImage = async (b64, note) => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/search/image", { image_base64: b64, note });
      setResult(data);
      setTimeout(() => {
        document
          .getElementById("results-anchor")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const onSubmitUrl = async (url, note) => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/search/url", { url, note });
      setResult(data);
      setTimeout(() => {
        document
          .getElementById("results-anchor")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-8 grid lg:grid-cols-12 gap-10">
          {/* Left copy */}
          <div className="lg:col-span-7 space-y-7">
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-ink text-linen tape-label">
                IA · Vision shopping
              </span>
              <span className="tape-label text-ink/50">v1.0 — fr</span>
            </div>

            <h1 className="font-display font-black text-[2.6rem] sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Vu sur Pinterest.
              <br />
              <span className="text-poppy">Acheté</span> en 2 clics.
            </h1>

            <p className="text-base md:text-lg text-ink/70 max-w-xl leading-relaxed">
              Collez un lien Instagram, TikTok, Pinterest — ou glissez une capture d'écran.
              Notre IA identifie le vêtement, l'objet ou la déco, puis vous dit{" "}
              <span className="font-bold text-ink">où l'acheter</span> sur Amazon, Zalando,
              Shein, Etsy, AliExpress…
            </p>

            <div className="flex flex-wrap items-center gap-5 text-sm text-ink/60">
              <Feat icon={Camera} label="Capture d'écran" />
              <Feat icon={Link2} label="Lien social" />
              <Feat icon={Sparkles} label="GPT Vision" />
              <Feat icon={Star} label="Favoris & alertes" />
            </div>
          </div>

          {/* Right search box */}
          <div className="lg:col-span-5">
            <SearchBox
              onSubmitImage={onSubmitImage}
              onSubmitUrl={onSubmitUrl}
              loading={loading}
            />
            <div className="mt-3 text-xs text-ink/50">
              <span className="tape-label">Astuce —</span> sur Pinterest, cliquez sur les{" "}
              <code className="px-1 bg-mist">…</code> puis "Copier le lien" du Pin.
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="border-y border-ink/15 bg-white">
          <div className="marquee-wrap py-3">
            <div className="marquee-track">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="flex items-center gap-10 pr-10">
                  {[
                    "AMAZON",
                    "ZALANDO",
                    "SHEIN",
                    "ETSY",
                    "ALIEXPRESS",
                    "VEEPEE",
                    "LEBONCOIN",
                    "GOOGLE SHOPPING",
                  ].map((m) => (
                    <span
                      key={m + i}
                      className="font-display font-black text-2xl tracking-tight text-ink/90"
                    >
                      {m} <span className="text-poppy">✦</span>
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Results anchor */}
      <div id="results-anchor" />

      {/* Results */}
      {(loading || result) && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
          {loading && <LoadingPanel />}
          {!loading && result && (
            <ResultsView result={result} onRequireAuth={onRequireAuth} />
          )}
        </section>
      )}

      {/* Trending */}
      {!result && !loading && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-16">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="tape-label text-poppy">03 / TENDANCES</div>
              <h2 className="font-display font-black text-3xl md:text-4xl">
                Les inspirations du moment
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {trending.map((t, i) => (
              <article
                key={i}
                data-testid={`trending-card-${i}`}
                className="card-brut overflow-hidden group"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={t.image}
                    alt={t.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                  />
                </div>
                <div className="p-4 border-t border-ink/10">
                  <div className="font-display font-bold text-base leading-tight">
                    {t.title}
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <div className="flex flex-wrap gap-1">
                      {t.tags.map((tg) => (
                        <span key={tg} className="text-[10px] tape-label text-ink/60">
                          #{tg}
                        </span>
                      ))}
                    </div>
                    <ArrowRight className="w-4 h-4 text-poppy" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* How it works */}
      {!result && !loading && (
        <section className="bg-ink text-linen mt-10">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid md:grid-cols-3 gap-8">
            <Step
              n="01"
              title="Importez"
              text="Une capture d'écran ou un lien Pinterest, Instagram, TikTok."
            />
            <Step
              n="02"
              title="L'IA analyse"
              text="GPT Vision identifie le produit, ses couleurs, ses matières et son style."
            />
            <Step
              n="03"
              title="Achetez"
              text="On vous renvoie vers les boutiques pertinentes — en un clic."
            />
          </div>
        </section>
      )}
    </main>
  );
}

function Feat({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="w-4 h-4 text-poppy" />
      <span className="font-bold">{label}</span>
    </div>
  );
}

function Step({ n, title, text }) {
  return (
    <div className="space-y-2">
      <div className="tape-label text-electric">{n}</div>
      <div className="font-display font-black text-2xl">{title}</div>
      <p className="text-linen/70 leading-relaxed">{text}</p>
    </div>
  );
}

function LoadingPanel() {
  return (
    <div
      data-testid="loading-panel"
      className="bg-white border border-ink p-8 md:p-12 shadow-brut text-center"
    >
      <div className="inline-flex items-center gap-3">
        <div className="w-3 h-3 bg-poppy animate-pulse" />
        <div className="w-3 h-3 bg-ink animate-pulse [animation-delay:120ms]" />
        <div className="w-3 h-3 bg-electric animate-pulse [animation-delay:240ms]" />
      </div>
      <div className="mt-4 font-display font-black text-2xl">L'IA analyse l'image…</div>
      <div className="text-sm text-ink/60 mt-1">
        Identification du produit, des couleurs, du style et des magasins.
      </div>
    </div>
  );
}
